/**
 * The bar that says "you are not you".
 *
 * An impersonated session looks exactly like an ordinary one — same chrome,
 * same data, the customer's name in the corner — which is precisely the danger.
 * The operator has to be unable to forget, so this is deliberately loud,
 * persistent, and at the very top of the shell above everything else.
 *
 * It renders nothing at all for an ordinary session, so mounting it is free.
 *
 * Nothing here is a security control. Read-only is enforced twice server-side —
 * reduced at mint time by the post-login Action and again per request by the
 * authorizer — and the expiry is enforced by the authorizer too. The banner's
 * job is to make an enforced boundary legible, so a refused write reads as "of
 * course, I'm read-only" rather than as a bug.
 */
import * as React from "react";
import { ShieldAlert } from "lucide-react";
import { getImpersonationState } from "@authe/fe-commons/auth";
import type { AutheClaims } from "@authe/fe-commons/auth";

export interface ImpersonationBannerProps {
  claims: AutheClaims | null | undefined;
  /** The token's top-level `sub` — the user being impersonated. */
  targetSub?: string | null;
  /**
   * Who that is, in words. A raw `google-oauth2|1020…` subject is not something
   * an operator recognises at a glance — and several accounts can share one
   * display name, so the email rides along.
   */
  targetName?: string | null;
  targetEmail?: string | null;
  /**
   * Why an impersonation handoff was refused at boot, if one was.
   *
   * Without this, a handoff that expired — the commonest failure, since they
   * die in minutes — drops the operator on an ordinary login screen with no
   * hint that the thing they clicked did anything at all.
   */
  failure?: string | null;
  /**
   * Ends the session in this tab. Omit it and the bar carries no control — the
   * `!session` mount has nothing to end.
   *
   * Until this existed the only ways out were closing the tab or waiting for the
   * expiry, and an operator who had finished with a customer account simply left
   * it open: a live read/write session onto someone else's data, sitting in a
   * tab strip. The way out has to be where the warning is.
   */
  onEnd?: () => void;
  /** Clock injection for tests. */
  now?: number;
}

/** Minutes until `iso`, or null when there is no expiry / it has passed. */
function minutesLeft(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return null;
  const mins = Math.ceil((ts - now) / 60_000);
  return mins > 0 ? mins : null;
}

/**
 * The bar paints its own background (amber, or destructive once expired), so the
 * control inherits `currentColor` rather than taking a themed Button variant —
 * which is built for the app's surfaces and reads as invisible on both of these.
 */
const END_BUTTON =
  "rounded border border-current/50 px-2 py-0.5 text-xs font-semibold hover:bg-black/10 " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current";

/** Tab-title marker, so the impersonated tab is recognisable from the tab strip. */
export const IMPERSONATION_TITLE_PREFIX = "[Impersonating] ";

export function ImpersonationBanner({
  claims,
  targetSub,
  targetName,
  targetEmail,
  failure,
  onEnd,
  now: fixedNow,
}: ImpersonationBannerProps): React.ReactElement | null {
  // Two-step, because one stray click would throw away a session that only the
  // admin portal can re-issue (the handoff is one-time). Inline rather than a
  // dialog: the bar is already the loudest thing on screen, and a modal over an
  // impersonated page is exactly the wrong place to take the operator's eye off
  // whose account they are in.
  const [confirming, setConfirming] = React.useState(false);
  // A live clock, so "Ends in N min" counts down and the bar turns red when the
  // session actually ends — not only when something else happens to re-render.
  const [tick, setTick] = React.useState(() => Date.now());
  const now = fixedNow ?? tick;
  const state = getImpersonationState(claims, { targetSub, now });
  // No roles at all in the tenant this session opened in. `undefined` is not the
  // same as empty: a claim that never arrived says nothing, and claiming "no
  // access" from ignorance is the failure this line exists to prevent.
  const rolePermissions = (claims as { role_permissions?: Record<string, unknown> } | null)
    ?.role_permissions;
  const noRoles = rolePermissions != null && Object.keys(rolePermissions).length === 0;
  const active = state.active;
  const bannerRef = React.useRef<HTMLDivElement>(null);
  // The shell occupies the remaining viewport, not a second full viewport
  // below a multi-line banner. Otherwise the document scrolls and the sticky
  // warning covers the navigation (particularly at narrow widths).
  React.useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const measure = () => document.documentElement.style.setProperty(
      "--impersonation-banner-height", `${banner.getBoundingClientRect().height}px`,
    );
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(banner);
    return () => { observer?.disconnect(); document.documentElement.style.removeProperty("--impersonation-banner-height"); };
  }, [active, failure]);

  React.useEffect(() => {
    if (!active || fixedNow !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [active, fixedNow]);

  // The operator has this tab open beside their own admin portal; the tab strip
  // is where "which one is the customer" gets decided. The router rewrites the
  // title per page, so re-apply the marker whenever it is dropped.
  React.useEffect(() => {
    if (!active || typeof document === "undefined") return;
    const mark = () => {
      if (!document.title.startsWith(IMPERSONATION_TITLE_PREFIX)) {
        document.title = IMPERSONATION_TITLE_PREFIX + document.title;
      }
    };
    mark();
    const titleEl = document.querySelector("title");
    const observer = titleEl ? new MutationObserver(mark) : null;
    if (titleEl && observer) observer.observe(titleEl, { childList: true });
    return () => {
      observer?.disconnect();
      if (document.title.startsWith(IMPERSONATION_TITLE_PREFIX)) {
        document.title = document.title.slice(IMPERSONATION_TITLE_PREFIX.length);
      }
    };
  }, [active]);

  // A refused handoff produces no impersonated session, so it is its own branch
  // rather than a variant of the bar below.
  if (!state.active) {
    if (!failure) return null;
    return (
      <div
        ref={bannerRef}
        role="alert"
        className="sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground"
      >
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        <span>Impersonation could not start: {failure}</span>
        {/* A refused handoff falls through to the ordinary login, and an
            operator who also uses the dashboard is silently signed straight
            back in AS THEMSELVES — same chrome, same name in the corner. Without
            this line that reads exactly like a working impersonation. */}
        {claims ? (
          <strong>You are signed in as yourself, not as the customer.</strong>
        ) : null}
        <span className="opacity-80">Start a new session from the admin portal.</span>
      </div>
    );
  }

  const remaining = minutesLeft(state.expiresAt, now);

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      className={[
        "sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2",
        "text-sm font-medium",
        state.expired
          ? "bg-destructive text-destructive-foreground"
          : "bg-amber-500 text-black dark:text-black",
      ].join(" ")}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />

      {state.expired ? (
        <span>
          This impersonation session has <strong>expired</strong>. Requests are being
          refused — close this tab and start a new session.
        </span>
      ) : (
        <>
          <span title={state.targetSub ?? undefined}>
            You are viewing this account as{" "}
            <strong>{targetName || targetEmail || state.targetSub || "another user"}</strong>
            {/* A login with no display name has its email as its name. */}
            {targetName && targetEmail && targetName !== targetEmail ? <> ({targetEmail})</> : null}
            {state.accessLevel === "read_only" ? " — read-only" : " — read/write"}.
          </span>
          {targetName || targetEmail ? (
            <span className="font-mono text-xs opacity-70">{state.targetSub}</span>
          ) : null}
          {/* A session can be opened in a tenant the target holds no role in —
              the vend accepts a tenant listed on their identity record, and role
              resolution then finds none. Every read 403s and the product simply
              looks empty, which reads as "this customer has no data" rather than
              "you are in the wrong tenant". Say it once, here. */}
          {noRoles ? (
            <span className="opacity-90">
              They hold no role in this tenant, so most reads will be refused — connect again in a
              tenant they belong to.
            </span>
          ) : null}
          {remaining !== null ? (
            <span className="opacity-80">Ends in {remaining} min.</span>
          ) : null}
        </>
      )}

      {state.reason ? <span className="opacity-80">Reason: {state.reason}</span> : null}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {state.sessionId ? (
          <span className="font-mono text-xs opacity-70">{state.sessionId}</span>
        ) : null}
        {onEnd ? (
          confirming ? (
            <>
              <span className="text-xs">End it now?</span>
              <button type="button" className={END_BUTTON} onClick={onEnd}>
                {state.expired ? "Close out" : "Yes, end session"}
              </button>
              <button
                type="button"
                className={END_BUTTON}
                onClick={() => setConfirming(false)}
              >
                Keep working
              </button>
            </>
          ) : (
            <button type="button" className={END_BUTTON} onClick={() => setConfirming(true)}>
              {state.expired ? "Close out" : "End session"}
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}
